import React, { useState, useEffect } from 'react';
import { useNavigate, Link } from 'react-router-dom';
import PageContainer from '../components/common/PageContainer';
import storeApi from '../services/storeApi';
import { imagePath } from '../utils/imagePath';

/**
 * CartPage - Shopping cart page
 * Follows React Rules: Side effects in useEffect, proper state management
 */
const CartPage = () => {
  const navigate = useNavigate();
  
  const [loading, setLoading] = useState(true);
  const [cart, setCart] = useState(null);
  const [error, setError] = useState(null);
  const [updating, setUpdating] = useState({});

  // Fetch cart
  useEffect(() => {
    const fetchCart = async () => {
      try {
        setLoading(true);
        setError(null);
        const cartData = await storeApi.getCart();
        setCart(cartData);
      } catch (err) {
        console.error('Error fetching cart:', err);
        if (err.response?.status === 401) {
          // Send logged-out users to the login flow (matches MessagesPage),
          // instead of leaving them on a bare cart error state.
          navigate('/?auth=login');
          return;
        }
        setError('Failed to load cart. Please try again.');
      } finally {
        setLoading(false);
      }
    };

    fetchCart();
  }, [navigate]);

  const handleUpdateQuantity = async (itemId, newQuantity) => {
    if (newQuantity < 1) {
      handleRemoveItem(itemId);
      return;
    }

    try {
      setUpdating(prev => ({ ...prev, [itemId]: true }));
      await storeApi.updateCartItem(itemId, newQuantity);
      
      // Refresh cart
      const updatedCart = await storeApi.getCart();
      setCart(updatedCart);
    } catch (error) {
      console.error('Error updating cart item:', error);
      alert('Failed to update quantity');
    } finally {
      setUpdating(prev => ({ ...prev, [itemId]: false }));
    }
  };

  const handleRemoveItem = async (itemId) => {
    if (!window.confirm('Remove this item from cart?')) return;

    try {
      setUpdating(prev => ({ ...prev, [itemId]: true }));
      await storeApi.removeFromCart(itemId);
      
      // Refresh cart
      const updatedCart = await storeApi.getCart();
      setCart(updatedCart);
    } catch (error) {
      console.error('Error removing cart item:', error);
      alert('Failed to remove item');
    } finally {
      setUpdating(prev => ({ ...prev, [itemId]: false }));
    }
  };

  const handleCheckout = () => {
    navigate('/store/checkout');
  };

  if (loading) {
    return (
      <PageContainer>
        <div className="text-center py-5">
          <p>Loading cart...</p>
        </div>
      </PageContainer>
    );
  }

  if (error) {
    return (
      <PageContainer>
        <div className="text-center py-5">
          <p className="text-danger">{error}</p>
          <Link to="/store/shop" className="btn">Continue Shopping</Link>
        </div>
      </PageContainer>
    );
  }

  if (!cart || !cart.items || cart.items.length === 0) {
    return (
      <PageContainer>
        <div className="cart-area pt-60 pb-60">
          <div className="container">
            <div className="row">
              <div className="col-lg-12">
                <div className="cart-empty text-center">
                  <h2>Your cart is empty</h2>
                  <p>Add some products to your cart to continue shopping.</p>
                  <Link to="/store/shop" className="btn">Continue Shopping</Link>
                </div>
              </div>
            </div>
          </div>
        </div>
      </PageContainer>
    );
  }

  return (
    <PageContainer>
      <div className="cart-area pt-60 pb-60">
        <div className="container">
          <div className="row">
            <div className="col-lg-12">
              <div className="cart-table-content">
                <div className="table-content table-responsive">
                  <table>
                    <thead>
                      <tr>
                        <th>Image</th>
                        <th>Product Name</th>
                        <th>Price</th>
                        <th>Quantity</th>
                        <th>Subtotal</th>
                        <th>Action</th>
                      </tr>
                    </thead>
                    <tbody>
                      {cart.items.map((item) => (
                        <tr key={item.id}>
                          <td className="product-thumbnail">
                            <Link to={`/store/product/${item.product.slug}`}>
                              <img
                                src={item.product.primary_image_url || imagePath('product/large-size/1.jpg')}
                                alt={item.product.name}
                                onError={(e) => {
                                  e.target.src = imagePath('product/large-size/1.jpg');
                                }}
                              />
                            </Link>
                          </td>
                          <td className="product-name">
                            <Link to={`/store/product/${item.product.slug}`}>
                              {item.product.name}
                            </Link>
                            {item.product.manufacturer && (
                              <p className="manufacturer">{item.product.manufacturer.name}</p>
                            )}
                          </td>
                          <td className="product-price">
                            <span>PKR {parseFloat(item.product.price).toFixed(2)}</span>
                          </td>
                          <td className="product-quantity">
                            <div className="cart-plus-minus">
                              <button
                                onClick={() => handleUpdateQuantity(item.id, item.quantity - 1)}
                                disabled={updating[item.id]}
                              >
                                -
                              </button>
                              <input type="text" value={item.quantity} readOnly />
                              <button
                                onClick={() => handleUpdateQuantity(item.id, item.quantity + 1)}
                                disabled={updating[item.id]}
                              >
                                +
                              </button>
                            </div>
                          </td>
                          <td className="product-subtotal">
                            <span>PKR {parseFloat(item.subtotal).toFixed(2)}</span>
                          </td>
                          <td className="product-remove">
                            <button
                              onClick={() => handleRemoveItem(item.id)}
                              disabled={updating[item.id]}
                              className="remove-btn"
                            >
                              <i className="fa fa-times"></i>
                            </button>
                          </td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              </div>

              <div className="row">
                <div className="col-lg-12">
                  <div className="cart-shiping-update-wrapper">
                    <div className="cart-shiping-update">
                      <Link to="/store/shop" className="btn">Continue Shopping</Link>
                    </div>
                    <div className="cart-clear">
                      <button className="btn" onClick={() => {
                        if (window.confirm('Clear all items from cart?')) {
                          // Clear cart by removing all items
                          cart.items.forEach(item => {
                            storeApi.removeFromCart(item.id).catch(console.error);
                          });
                          setCart({ ...cart, items: [] });
                        }
                      }}>
                        Clear Cart
                      </button>
                    </div>
                  </div>
                </div>
              </div>

              <div className="row">
                <div className="col-lg-4 col-md-12">
                  {/* Coupon code can be added here */}
                </div>
                <div className="col-lg-4 col-md-12 ml-auto">
                  <div className="cart-tax">
                    <div className="title-wrap">
                      <h4>Cart Totals</h4>
                    </div>
                    <div className="total-tax">
                      <p>
                        Subtotal: <span>PKR {parseFloat(cart.subtotal || 0).toFixed(2)}</span>
                      </p>
                      <p>
                        Total Items: <span>{cart.total_items || 0}</span>
                      </p>
                      <h4>
                        Total: <span>PKR {parseFloat(cart.subtotal || 0).toFixed(2)}</span>
                      </h4>
                    </div>
                    <div className="checkout-btn">
                      <button className="btn" onClick={handleCheckout}>
                        Proceed to Checkout
                      </button>
                    </div>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>
    </PageContainer>
  );
};

export default CartPage;
